// Рендер эмодзи через локально забандленный Noto Emoji (не системный шрифт ОС) — единый
// вид на Windows/Android/вебе. Статичный набор: clients/web/public/noto-emoji/svg/ (Apache 2.0,
// github.com/googlefonts/noto-emoji). Анимированный набор — только пересечение с эмодзи из
// EmojiPicker (см. ANIMATED_CODEPOINTS): clients/web/public/noto-emoji/animated/ (CC BY 4.0,
// googlefonts.github.io/noto-emoji-animation), полный набор анимаций весит сотни МБ и не
// бандлится целиком. Наличие файла в обоих случаях проверяется браузером естественно —
// если ссылка не найдётся, <Emoji> откатится на следующий вариант (см. Emoji.tsx).

// Intl.Segmenter отсутствует в типах при lib=ES2020 (tsconfig), хотя в браузерах есть.
type Segmenter = { segment(input: string): Iterable<{ segment: string }> };
type SegmenterCtor = new (locale: string, options: { granularity: 'grapheme' }) => Segmenter;
const NativeSegmenter = (Intl as unknown as { Segmenter?: SegmenterCtor }).Segmenter;

/** Разбивает строку на графемы (эмодзи-последовательности как один элемент). */
export function graphemes(text: string): string[] {
  if (NativeSegmenter) {
    return [...new NativeSegmenter('ru', { granularity: 'grapheme' }).segment(text)].map((s) => s.segment);
  }
  return [...text];
}

/**
 * true, если графема — эмодзи (а не обычный текстовый символ/буква).
 * \p{Extended_Pictographic} не покрывает всё: флаги (пары Regional_Indicator, например 🇷🇺)
 * и keycap-последовательности (0️⃣–9️⃣, #️⃣, *️⃣ — цифра/символ + U+20E3) у него не входят,
 * хотя SVG для них в наборе Noto есть — без этой добавки они тихо падали на системный шрифт
 * ОС вместо картинки (непоследовательный вид, тот самый баг "не все смайлики отображаются").
 */
export function isEmojiGrapheme(g: string): boolean {
  return /\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u.test(g);
}

/** true, если строка — ровно один эмодзи-графем (для крупного показа сообщения). */
export function isSoloEmoji(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  const g = graphemes(t);
  return g.length === 1 && isEmojiGrapheme(g[0]);
}

/**
 * Кодпоинты графемы -> ключ имени файла Noto (emoji_u<hex>[_<hex>...].svg). Variation
 * selectors (U+FE0E/FE0F) вырезаны — их нет и в исходных именах файлов набора. Каждый
 * кодпоинт дополняется нулями слева до 4 hex-цифр (printf %04x) — это реальный формат имён
 * файлов Noto: например ©️ лежит как emoji_u00a9.svg, а не emoji_ua9.svg. Без паддинга все
 * эмодзи с кодпоинтом < U+1000 (клавиши-кейкапы 0-9/#/*, ©️, ®️ и т.п.) тихо не находились.
 */
function codepointKey(cluster: string): string | null {
  const hex: string[] = [];
  for (const ch of cluster) {
    const cp = ch.codePointAt(0);
    if (cp == null || cp === 0xfe0e || cp === 0xfe0f) continue;
    hex.push(cp.toString(16).padStart(4, '0'));
  }
  return hex.length ? hex.join('_') : null;
}

export function staticEmojiSrc(cluster: string): string | null {
  const key = codepointKey(cluster);
  return key ? `/noto-emoji/svg/emoji_u${key}.svg` : null;
}

// Пересечение полного набора EMOJI_CATEGORIES (1911 эмодзи, см. lib/emojiCategories.ts)
// с тем, что реально нашлось на fonts.gstatic.com/s/e/notoemoji/latest/<key>/512.webp
// (тот же CDN, что раздаёт googlefonts.github.io/noto-emoji-animation) — из 1911 анимация
// существует у 614, остальным Google Fonts её не делал. Список сгенерирован сверкой по
// каждому кодпоинту (не руками) и должен 1:1 совпадать с содержимым
// clients/web/public/noto-emoji/animated/ — при добавлении новых файлов туда перегенерировать.
const ANIMATED_CODEPOINTS = new Set([
  '00a9', '00ae', '1f192', '1f193', '1f195', '1f197', '1f198', '1f199', '1f300', '1f304',
  '1f305', '1f307', '1f308', '1f30a', '1f30b', '1f30c', '1f30d', '1f30e', '1f30f', '1f31a',
  '1f31b', '1f31c', '1f31d', '1f31e', '1f31f', '1f321', '1f327', '1f329', '1f32a', '1f32c',
  '1f32d', '1f32e', '1f32f', '1f331', '1f332', '1f335', '1f337', '1f338', '1f339', '1f33c',
  '1f33d', '1f33f', '1f340', '1f341', '1f342', '1f343', '1f344', '1f345', '1f347', '1f348',
  '1f349', '1f34a', '1f34b', '1f34d', '1f34e', '1f350', '1f352', '1f353', '1f354', '1f355',
  '1f357', '1f35c', '1f35d', '1f35e', '1f361', '1f362', '1f366', '1f368', '1f369', '1f36a',
  '1f373', '1f375', '1f377', '1f379', '1f37b', '1f37c', '1f37d', '1f37e', '1f37f', '1f381',
  '1f382', '1f383', '1f386', '1f388', '1f389', '1f38a', '1f393', '1f39e', '1f39f', '1f3a0',
  '1f3a1', '1f3a2', '1f3a3', '1f3ac', '1f3ad', '1f3af', '1f3b0', '1f3b1', '1f3b2', '1f3b3',
  '1f3b6', '1f3b7', '1f3b8', '1f3ba', '1f3bb', '1f3be', '1f3bf', '1f3c0', '1f3c1', '1f3c6',
  '1f3c9', '1f3cd', '1f3ce', '1f3cf', '1f3d1', '1f3d2', '1f3d3', '1f3d5', '1f3da', '1f3de',
  '1f3e0', '1f3f3', '1f3f4', '1f3f8', '1f400', '1f402', '1f405', '1f407', '1f409', '1f40a',
  '1f40c', '1f40d', '1f40e', '1f410', '1f412', '1f413', '1f415', '1f415_200d_1f9ba', '1f416', '1f419',
  '1f41b', '1f41c', '1f41d', '1f41e', '1f41f', '1f421', '1f422', '1f423', '1f424', '1f425',
  '1f426', '1f426_200d_1f525', '1f426_200d_2b1b', '1f427', '1f429', '1f42c', '1f42e', '1f431', '1f433', '1f438',
  '1f43a', '1f43b', '1f43c', '1f43e', '1f43f', '1f440', '1f441', '1f442', '1f443', '1f446',
  '1f447', '1f448', '1f449', '1f44a', '1f44b', '1f44c', '1f44d', '1f44e', '1f44f', '1f450',
  '1f451', '1f45f', '1f460', '1f463', '1f479', '1f47b', '1f47d', '1f47e', '1f47f', '1f480',
  '1f483', '1f485', '1f48b', '1f48c', '1f48d', '1f48e', '1f490', '1f493', '1f494', '1f495',
  '1f496', '1f497', '1f498', '1f499', '1f49a', '1f49b', '1f49c', '1f49d', '1f49e', '1f49f',
  '1f4a1', '1f4a3', '1f4a5', '1f4a7', '1f4a9', '1f4aa', '1f4ab', '1f4ac', '1f4af', '1f4b8',
  '1f4bb', '1f4bf', '1f4c8', '1f4c9', '1f4ca', '1f4da', '1f4df', '1f4e3', '1f4e6', '1f4f4',
  '1f4f7', '1f4f8', '1f4fa', '1f50b', '1f50c', '1f50e', '1f512', '1f514', '1f525', '1f52e',
  '1f54a', '1f573', '1f577', '1f587', '1f590', '1f595', '1f596', '1f5a4', '1f5a8', '1f5d1',
  '1f5e3', '1f5ef', '1f5f3', '1f5ff', '1f600', '1f601', '1f602', '1f603', '1f604', '1f605',
  '1f606', '1f607', '1f608', '1f609', '1f60a', '1f60b', '1f60c', '1f60d', '1f60e', '1f60f',
  '1f610', '1f611', '1f612', '1f613', '1f614', '1f615', '1f616', '1f617', '1f618', '1f619',
  '1f61a', '1f61b', '1f61c', '1f61d', '1f61e', '1f61f', '1f620', '1f621', '1f622', '1f623',
  '1f624', '1f625', '1f626', '1f627', '1f628', '1f629', '1f62a', '1f62b', '1f62c', '1f62d',
  '1f62e', '1f62e_200d_1f4a8', '1f62f', '1f630', '1f631', '1f632', '1f633', '1f634', '1f635', '1f635_200d_1f4ab',
  '1f636', '1f636_200d_1f32b', '1f637', '1f638', '1f639', '1f63a', '1f63b', '1f63c', '1f63d', '1f63e',
  '1f63f', '1f640', '1f641', '1f642', '1f642_200d_2194', '1f642_200d_2195', '1f643', '1f644', '1f648', '1f649',
  '1f64a', '1f64c', '1f64f', '1f680', '1f682', '1f68c', '1f695', '1f697', '1f69a', '1f69c',
  '1f6a6', '1f6a7', '1f6a8', '1f6a9', '1f6ae', '1f6b2', '1f6ce', '1f6d1', '1f6d2', '1f6d8',
  '1f6df', '1f6e0', '1f6eb', '1f6ec', '1f6f6', '1f6f8', '1f6f9', '1f6fc', '1f90c', '1f90d',
  '1f90e', '1f90f', '1f910', '1f911', '1f912', '1f913', '1f914', '1f915', '1f916', '1f917',
  '1f918', '1f919', '1f91a', '1f91b', '1f91c', '1f91d', '1f91e', '1f91f', '1f920', '1f921',
  '1f922', '1f923', '1f924', '1f925', '1f927', '1f928', '1f929', '1f92a', '1f92b', '1f92c',
  '1f92d', '1f92e', '1f92f', '1f932', '1f933', '1f940', '1f941', '1f942', '1f947', '1f948',
  '1f949', '1f94b', '1f94d', '1f94e', '1f94f', '1f951', '1f952', '1f953', '1f954', '1f955',
  '1f957', '1f95d', '1f95e', '1f962', '1f966', '1f967', '1f968', '1f96c', '1f96d', '1f970',
  '1f971', '1f972', '1f973', '1f974', '1f975', '1f976', '1f978', '1f979', '1f97a', '1f980',
  '1f981', '1f982', '1f984', '1f985', '1f987', '1f988', '1f989', '1f98a', '1f98b', '1f98d',
  '1f98e', '1f994', '1f995', '1f996', '1f997', '1f998', '1f99a', '1f99d', '1f99e', '1f99f',
  '1f9a0', '1f9a5', '1f9a6', '1f9a7', '1f9a9', '1f9ad', '1f9ae', '1f9b4', '1f9b5', '1f9b6',
  '1f9bb', '1f9be', '1f9bf', '1f9c0', '1f9c2', '1f9c3', '1f9c4', '1f9c5', '1f9c9', '1f9cb',
  '1f9d0', '1f9e0', '1f9e1', '1f9e6', '1f9f9', '1fa70', '1fa75', '1fa76', '1fa77', '1fa78',
  '1fa7a', '1fa81', '1fa82', '1fa83', '1fa84', '1fa85', '1fa87', '1fa89', '1fa8a', '1fa8e',
  '1fa8f', '1fa90', '1fa94', '1fa95', '1fa99', '1faa4', '1faa8', '1faa9', '1faab', '1faad',
  '1fab0', '1fab1', '1fab3', '1fab7', '1fabb', '1fabc', '1fabe', '1fabf', '1fac0', '1fac2',
  '1fac6', '1fac8', '1facd', '1facf', '1fad0', '1fad1', '1fad6', '1fad7', '1fad8', '1fada',
  '1fadb', '1fadc', '1fadf', '1fae0', '1fae1', '1fae2', '1fae3', '1fae4', '1fae5', '1fae6',
  '1fae7', '1fae8', '1fae9', '1faea', '1faef', '1faf0', '1faf1', '1faf2', '1faf3', '1faf4',
  '1faf5', '1faf6', '1faf7', '1faf8', '203c', '2049', '2122', '231b', '23f0', '23f3',
  '2601', '2602', '2603', '2604', '2615', '261d', '2622', '2623', '262e', '262f',
  '2639', '263a', '2648', '2649', '264a', '264b', '264c', '264d', '264e', '264f',
  '2650', '2651', '2652', '2653', '265f', '2660', '2665', '2668', '267e', '2693',
  '2696', '2699', '26a0', '26a1', '26bd', '26be', '26c4', '26c5', '26ce', '26d3',
  '26d3_200d_1f4a5', '26f3', '26f5', '26f8', '26fd', '2702', '2705', '2708', '270a', '270b',
  '270c', '270d', '270f', '2728', '2744', '274c', '2753', '2757', '2763', '2764',
  '2764_200d_1f525', '2764_200d_1fa79', '2795', '2b50',
]);

export function animatedEmojiSrc(cluster: string): string | null {
  const key = codepointKey(cluster);
  return key && ANIMATED_CODEPOINTS.has(key) ? `/noto-emoji/animated/${key}.webp` : null;
}
